import { Location } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    CanDeactivate,
    NavigationEnd,
    Params,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { IOption } from '@cmps/ng-select';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import subDays from 'date-fns/subDays';
import { combineLatest, interval, Observable, Subscription } from 'rxjs';
import { filter, map, pairwise, startWith, tap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { FocusDto, NoteCategory } from '../../coaching-base/general';
import {
    CoachingSwitchboardService,
    SwitchboardObservationComm,
} from '../../coaching-base/switchboard';
import { getDialogItems } from '../../coaching-commitment/functions/get-dialog-items.function';
import { goToDraftOrCommitment } from '../../coaching-commitment/functions/go-to-draft-or-commitment.function';
import { prepStatuses } from '../../coaching-commitment/functions/prep-statuses.function';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
    composeCommitmentSummaryItemIdentifier,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    StatusView,
    TabRefId,
} from '../../coaching-detail';
import {
    navigateToDetail,
    navigateToExistingCommitment,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    navigateToExistingDraftCommitment,
    navigateToNewDraftCommitment,
    replaceStateToExistingObservation,
} from '../../coaching-detail/navigate';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { StatusViewType } from '../../coaching-detail/pillar-two/status-view-type.model';
import { ClickOptions } from '../../coaching-insight/click-options';
import { NoteDetailFormValue } from '../../coaching-note/note-detail-form-value.model';
import {
    composeTagIdentifierNext,
    FocusShallow,
    reconstructTagFromIdentifierNext,
} from '../../coaching-tags/tags/tags.model';
import { DateProviders } from '../../common/component/datetime-picker';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import {
    disuseStore,
    pluck,
    useDispatcher,
    useStore,
} from '../../store.decorator';
import { BringIntoFocusService } from '../bring-into-focus.service';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { CmpsForm, CmpsFormBuilder } from '../experimental.form';
import { ObservationFormDecoratorService } from '../form-decorator/observation-form-decorator.service';
import {
    ObservationCommitDialogComponent,
    ObservationCommitDialogItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ObservationCommitDialogItemStatus,
    ObservationCommitDialogResult,
    ObservationCommitOption,
} from '../observation-commit-dialog/observation-commit-dialog.component';
import { ObservationCommitmentUpsertEvent } from '../observation-commitment-upsert-event';
import { ObservationMandatoryFieldsDialogComponent } from '../observation-dialog-mandatory-fields/observation-dialog-mandatory-fields.component';
import { ObservationMandatoryFieldsDialogItem } from '../observation-dialog-mandatory-fields/observation-mandatory-fields-dialog-item.model';
import { TagGroupsObservationComponent } from '../observation-tag-groups/tag-groups.observation.component';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { determineCommitItems } from './determine-commit-items.function';
import { ObservationExtraOptions } from './observation-extra-options.model';
import { ObservationFormValue } from './observation-form-value.model';
import {
    rootDelete,
    rootDismissMessage,
    rootNavigate,
    rootReload,
    rootSave,
    rootSetMode,
} from './observation.action';
import { ObservationFormFields } from './observation.form';
import {
    AllowedActions,
    EnabledActions,
    NavigationScenario,
    ObservationFormDisplayProperties,
    ObservationMode,
    READONLY_OBSERVATION,
    ReadonlyObservation,
} from './observation.model';
import observationReducer from './observation.reducer';
import { RootState } from './observation.state';
import { observationIsValid } from './validation/observation-is-valid.function';

@Component({
    selector: '[cmpsObservationNext]',
    templateUrl: './observation.component.html',
    host: {
        '[attr.data-observationId]': 'observationId', // for use in observation review modal
        'data-e2e': 'component-observation',
    },
    providers: [DateProviders, ObservationFormDecoratorService],
    viewProviders: [BringIntoFocusService],
})
@TakeUntilDestroy()
export class ObservationComponent
    implements
        OnChanges,
        OnInit,
        AfterViewInit,
        OnDestroy,
        CanDeactivate<ObservationComponent>,
        StateToProps<RootState>,
        SwitchboardObservationComm
{
    state: RootState;
    @Input() storePathPrefix: string;
    @ViewChild(TagGroupsObservationComponent)
    _tagGroupsObservation: TagGroupsObservationComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ObservationMode = ObservationMode;
    /** @private */ _NoteCategory = NoteCategory;
    /** @private */ _form: CmpsForm<
        ObservationFormFields,
        ObservationFormValue
    >;

    /** @private */ _deliveryTypeOpts: IOption[];
    private _maxDateSub: Subscription;
    /** @private */ _maxDate: Date;
    /** @private */ _minDate: Date;
    private _userId: number;
    private _observationId: number;
    private _loadActive: boolean;
    /** @private */ _isGeneric: boolean;
    /** @private */ _activeMetric: number;
    private _latestCommitmentEventId: number;
    private _fromOa: boolean;

    // TODO revise. separate component for readonly
    @Input() mode: ObservationMode;
    @Input() userId: number;
    @Input() observationId: number;
    @Input() draftCommitmentId: string;
    @Input() commitmentId: number;
    @Input() displayProperties: ObservationFormDisplayProperties;
    @Input() filteredFocus: FocusShallow[];
    @Input() focus: FocusShallow;
    @Input() previousDisabled: boolean;
    @Output() previous: EventEmitter<void>;
    @Input() nextDisabled: boolean;
    @Output() next: EventEmitter<void>;
    @Output() allowedActionsChange: EventEmitter<AllowedActions>;
    @Output() enabledActionsChange: EventEmitter<EnabledActions>;
    @Input() extraOptions: ObservationExtraOptions;

    /** @private */ _untaggedTagGroupIsVisible: boolean;

    /** @private */ _attemptedToPublish: boolean;

    get pendingAny(): boolean {
        return this.state && Object.values(this.state.pending).some((e) => !!e);
    }

    get pending(): { config?: boolean; reload?: boolean } {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return this.state && this.state.pending;
    }

    get published(): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return this.state && this.state.event && this.state.event.published;
    }

    get dirty(): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return this._form && this._form.form && this._form.form.dirty;
    }

    constructor(
        private readonly _activatedRoute: ActivatedRoute,
        private _router: Router,
        private readonly _location: Location,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _authService: AuthService,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _formDecorator: ObservationFormDecoratorService,
        private readonly _focusService: BringIntoFocusService,
        @Optional() /** @private */
        readonly _dialogRef: CmpsDialogRef<any, any>,
        @Optional()
        @Inject(READONLY_OBSERVATION) /** @private */
        readonly _readonly: ReadonlyObservation,
    ) {
        this.mode = ObservationMode.DEFAULT;
        this.previous = new EventEmitter();
        this.next = new EventEmitter();
        this.allowedActionsChange = new EventEmitter();
        this.enabledActionsChange = new EventEmitter();
    }

    // TODO revise. separate component for readonly
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) {
            return;
        }

        if (this._readonly && this._form) {
            // eslint-disable-next-line no-prototype-builtins
            if (changes.hasOwnProperty('mode')) {
                if (this._readonly.value) {
                    this._form.form.disable({ emitEvent: false });
                } else {
                    // yes call both
                    this._form.form.enable({ emitEvent: false });
                    if (this.state.event.published) {
                        this._form.form.disable({ emitEvent: false });
                    }
                }
                // this._observationSwitchboardService.connectObservation(this);

                useDispatcher(this, rootSetMode({ mode: this.mode }));
            }
        }

        /// /////////////////

        // evaluate last so rootSetMode is always dispatched first
        if (
            this.mode !== ObservationMode.DEFAULT &&
            // eslint-disable-next-line no-prototype-builtins
            (changes.hasOwnProperty('userId') ||
                // eslint-disable-next-line no-prototype-builtins
                changes.hasOwnProperty('observationId'))
        ) {
            this._untaggedTagGroupIsVisible = false;

            useDispatcher(
                this,
                rootReload({
                    performerId: this.userId,
                    observationId: this.observationId,
                    observerId: this._authService.user.id,
                    loadActive: this._loadActive,
                    draftCommitmentId: this.draftCommitmentId,
                    commitmentId: this.commitmentId,
                    activeMetricId: this._activeMetric,
                    focus: this.focus,
                    extraOptions: this.extraOptions,
                }),
            );
        }
    }

    ngOnInit(): void {
        const path = 'coachingObservation.root';
        useStore(
            this,
            !this.storePathPrefix ? path : `${this.storePathPrefix}.${path}`,
            observationReducer.coachingObservation.root,
        );

        this._router.onSameUrlNavigation = 'reload'; // TODO revise. careful
        this._form = this._formBuilder.create<ObservationFormFields>(
            ObservationFormFields,
        );

        /* // programmatic changes must not emit events. since this if for checking for user input
        this._form.form.controls[ObservationFormFields.DURATION.name].valueChanges
            .pipe(
                tap(() => sub && sub.unsubscribe()),
                takeUntilDestroy(this)
            )
            .subscribe(); */

        CMPS_APP_DEF.ENV !== 'prod' &&
            this._form.form.valueChanges
                .pipe(
                    tap((value) => {
                        if (this._attemptedToPublish) {
                            const errors = observationIsValid(value, {
                                isPublishingFn: () => this._attemptedToPublish,
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                isSavedObservationFn: () =>
                                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                    this.state &&
                                    this.state.event &&
                                    this.state.event.eventId != null,
                                unFocusedContainerIsVisibleFn: () =>
                                    this._untaggedTagGroupIsVisible,
                            });
                            this._formDecorator.decorateDelta(errors);
                        }
                        console.log(value);
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();

        this._form.setValidators(ObservationFormFields.DURATION, [
            Validators.min(1),
            Validators.max(480),
        ]);

        this._switchboardService.observation.connect(this);
    }

    ngAfterViewInit(): void {
        // data attr for use in observation review modal
        const attr =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._readonly && this._readonly.value
                ? `[data-observationId="${this.observationId}"]`
                : '';

        this._focusService.registerMutatorSelector(
            `[cmpsObservationNext]${attr}`,
            () => {
                // TODO move. see .container-scroll in scss
                return window.matchMedia(
                    '(min-width: 1200px) and (min-height: 450px)',
                ).matches
                    ? '[cmpsObservationNext] .container-scroll'
                    : null;
            },
        );
    }

    ngOnDestroy(): void {
        disuseStore(this);

        this._router.onSameUrlNavigation = 'ignore'; // TODO revise. careful

        this.previous.complete();
        this.next.complete();
        this.allowedActionsChange.complete();
        this.enabledActionsChange.complete();

        this._formDecorator.unDecorate();
        this._switchboardService.observation.disconnect();
        this._focusService.stopObserving();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._form && this._form.destroy();
    }

    // TODO consolidate?
    canDeactivate(
        component: ObservationComponent,
        currentRoute: ActivatedRouteSnapshot,
        currentState?: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        return this._askToContinue(currentState, nextState);
    }

    stateToProps(prevState?: RootState): void {
        if (prevState.pending.reload && !this.state.pending.reload) {
            this._deliveryTypeOpts = this.state.coachingEvent.deliveryTypes.map(
                (e) => ({
                    value: e.deliveryTypeId,
                    label: e.deliveryTypeName,
                }),
            );

            if (this.state.event) {
                this._patchForm();
            }
        }

        if (prevState.pending.save && !this.state.pending.save) {
            this._patchForm();
            /** this._forceTriggerOppCommitmentsReload(); */

            // only reload here if it was/is existing observation, and no navigation action is pending
            if (
                prevState.event.eventId === this.state.event.eventId &&
                this.state.event.eventId != null &&
                !this.state.willNavigate
            ) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._switchboardService.pillarTwo.sbReload(
                    TabRefId.OBSERVATIONS,
                );
            }

            // only reload here if it is newly saved within a popup
            // this may be mergeable with NEWLY_SAVED/DEFAULT lower down
            if (
                this.mode === ObservationMode.POP_UP &&
                prevState.event.eventId == null &&
                this.state.event.eventId != null &&
                this._switchboardService.pillarTwo.connected()
            ) {
                this._switchboardService.pillarTwo
                    .sbReload(TabRefId.OBSERVATIONS)
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this._switchboardService.pillarTwo.sbSetStatuses(
                            [
                                {
                                    which: TabRefId.OBSERVATIONS,
                                    id: this.state.event.eventId,
                                    type: undefined,
                                    identifier: undefined,
                                    active: true,
                                },
                            ],
                        );
                    })
                    .then(() => {
                        this._switchboardService.pillarTwo.sbScrollIntoView(
                            TabRefId.OBSERVATIONS,
                            this.state.event.eventId,
                        );
                    })
                    .catch(() => null);
            }
        }

        if (!prevState.navigationScenario && this.state.navigationScenario) {
            if (
                this.state.navigationScenario ===
                    NavigationScenario.NEWLY_PUBLISHED_NAVIGATE ||
                this.state.navigationScenario ===
                    NavigationScenario.COMMITMENT ||
                this.state.navigationScenario === NavigationScenario.DELETE
            ) {
                this._switchboardService.observationReview.sbWillNavigate();
            }

            this._navigate();

            useDispatcher(this, rootNavigate({ scenario: undefined }));
        }

        if (!prevState.allowedActions !== this.state.allowedActions) {
            this.allowedActionsChange.emit(this.state.allowedActions);
        }

        if (!prevState.enabledActions !== this.state.enabledActions) {
            this.enabledActionsChange.emit(this.state.enabledActions);
        }
    }

    propsToDispatch(): void {
        useDispatcher(this, rootSetMode({ mode: this.mode }));

        // TODO change and check against this.state.mode
        if (this.mode === ObservationMode.DEFAULT) {
            this._paramsWatcher()
                .pipe(
                    tap(() => {
                        this._untaggedTagGroupIsVisible = false;

                        useDispatcher(
                            this,
                            rootReload({
                                performerId: this._userId,
                                observationId: this._observationId,
                                observerId: this._authService.user.id,
                                loadActive: this._loadActive,
                                draftCommitmentId: this.draftCommitmentId,
                                commitmentId: this.commitmentId,
                                activeMetricId: this._activeMetric,
                                isGeneric: this._isGeneric,
                                focus: this.focus,
                                extraOptions: this.extraOptions,
                            }),
                        );
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        } else {
            this._untaggedTagGroupIsVisible = false;

            useDispatcher(
                this,
                rootReload({
                    performerId: this.userId,
                    observationId: this.observationId,
                    observerId: this._authService.user.id,
                    loadActive: this._loadActive,
                    draftCommitmentId: this.draftCommitmentId,
                    commitmentId: this.commitmentId,
                    activeMetricId: this._activeMetric,
                    isGeneric: this._isGeneric,
                    focus: this.focus,
                    extraOptions: this.extraOptions,
                }),
            );
        }
    }

    sbCanCancelEdit(
        currentState?: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        return this._askToContinue(currentState, nextState);
    }

    sbCancelEdit(): void {
        this._patchForm();
    }

    /** @private **/ _onCreateFocusContainerClick(): void {
        this._tagGroupsObservation.onCreateFocusContainerClick();
    }

    publish(): void {
        this._attemptedToPublish = true;

        const items = getDialogItems(
            this._form.form.value,
            this.state?.event?.eventId != null,
            this.state.behaviours,
            this.state.subBehaviours,
            this._switchboardService,
        );

        const ret = observationIsValid(this._form.value, {
            isPublishingFn: () => this._attemptedToPublish,
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            isSavedObservationFn: () =>
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.state &&
                this.state.event &&
                this.state.event.eventId != null,
            unFocusedContainerIsVisibleFn: () =>
                this._untaggedTagGroupIsVisible,
        });
        const errors = ret.valid ? null : ret;
        this._formDecorator.decorate(errors);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (errors && errors.showDialog) {
            this._dialog.open(ObservationMandatoryFieldsDialogComponent, {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                data: {
                    isValidFocus: errors.dialogArgs.focusValid,
                    isInteraction: errors.dialogArgs.interactionValid,
                    invalidEvaluationWillingness: errors.dialogArgs.wttInvalid,
                    invalidEvaluationProficiency: errors.dialogArgs.pdInvalid,
                } as ObservationMandatoryFieldsDialogItem,
            });
        } else {
            if (
                this.extraOptions?.scenario ===
                    NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED ||
                this.extraOptions?.scenario ===
                    NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED ||
                this.extraOptions?.scenario ===
                    NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED ||
                this.extraOptions?.scenario ===
                    NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED
            ) {
                const data: CmpsConfirmDialogOptions = {
                    title: this._translateService.instant(
                        TranslationKeyMap._9479_CONFIRMATION,
                    ),
                    message: this._translateService.instant(
                        TranslationKeyMap._11044_WOULD_YOU_LIKE_TO_PUBLISH_THIS_OBSERVATION_AND_CONTINUE,
                    ),
                    actions: [
                        {
                            label: this._translateService.instant(
                                TranslationKeyMap._11039_PUBLISH_AND_CONTINUE,
                            ),
                            data: 0,
                        },
                        {
                            label: this._translateService.instant(
                                TranslationKeyMap._11040_PUBLISH_AND_CLOSE,
                            ),
                            data: 1,
                        },
                        {
                            label: this._translateService.instant(
                                TranslationKeyMap._121_CANCEL,
                            ),
                            data: 2,
                        },
                    ],
                };
                const ref = this._dialog.open(CmpsConfirmDialogComponent, {
                    data,
                });
                ref.result
                    .pipe(
                        tap((ret) => {
                            if (ret === 2) {
                                return;
                            }
                            this.extraOptions?.willPublish({
                                doNotNavigate: ret === 0,
                            });
                            this._publish(this.extraOptions?.scenario, {
                                option: ObservationCommitOption.PUBLISH,
                                /* doesn't matter if focus was supplied or not... use the focus from the first container */
                                behaviourId: items?.[0]?.BehaviourId,
                                subBehaviourId: items?.[0]?.SubBehaviourId,
                                items,
                            });
                        }),
                        takeUntilDestroy(this),
                    )
                    .subscribe();

                return;
            }

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            const focusSupplied =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.focus != null && this.focus.behaviourId != null;

            if (this.mode === ObservationMode.POP_UP && focusSupplied) {
                this._publish(
                    NavigationScenario.FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK,
                    {
                        option: ObservationCommitOption.PUBLISH,
                        behaviourId: this.focus.behaviourId,
                        subBehaviourId: this.focus.subBehaviourId,
                        items,
                    },
                );
                return;
            }

            if (
                this.mode === ObservationMode.POP_UP &&
                !focusSupplied &&
                items.length > 1
            ) {
                this._commitDialogPopUp(
                    NavigationScenario.NO_FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK,
                    items,
                );
                return;
            }

            this._confirmPublish(focusSupplied, items);
        }
    }

    private _confirmPublish(
        focusSupplied: boolean,
        items: ObservationCommitDialogItem[],
    ): void {
        const noFocusSuppliedAndSingleItem =
            this.mode === ObservationMode.POP_UP &&
            !focusSupplied &&
            items.length === 1;

        const message = noFocusSuppliedAndSingleItem
            ? TranslationKeyMap._9673_THE_FOCUS_YOU_SELECTED_IN_THE_OBSERVATION_WILL_BE_USED_FOR_THE_COMMITMENT__DO_YOU_WANT_TO_PROCEED_
            : TranslationKeyMap._9674_YOU_ARE_PUBLISHING_AN_OBSERVATION__DO_YOU_WANT_TO_UPDATE_OR_CREATE_A_COMMITMENT_;
        const labelYes = noFocusSuppliedAndSingleItem
            ? TranslationKeyMap._9297_YES
            : TranslationKeyMap._9672_JUST_PUBLISH;
        const labelNo = noFocusSuppliedAndSingleItem
            ? TranslationKeyMap._9298_NO
            : TranslationKeyMap._9671_COMMIT;

        const actionNo = (): void => {
            if (noFocusSuppliedAndSingleItem) {
                return;
            }

            let scenario = NavigationScenario.NEWLY_PUBLISHED_NAVIGATE;
            if (this.mode !== ObservationMode.DEFAULT && focusSupplied) {
                // reaching here probably means on-commitment and link-observation
                scenario =
                    NavigationScenario.FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK;
            }

            if (items.length === 1) {
                this._publish(scenario, {
                    option: ObservationCommitOption.PUBLISH,
                    behaviourId: items[0].BehaviourId,
                    subBehaviourId: items[0].SubBehaviourId,
                    items,
                });
            } else {
                this._commitDialogPopUp(scenario, items);
            }
        };
        const actionYes = (): void => {
            let scenario =
                NavigationScenario.FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK;

            if (noFocusSuppliedAndSingleItem) {
                scenario =
                    NavigationScenario.NO_FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK;
            }

            if (this.mode === ObservationMode.DEFAULT) {
                scenario = NavigationScenario.NEWLY_PUBLISHED_STAY;
            }

            this._publish(scenario, {
                option: ObservationCommitOption.PUBLISH,
                behaviourId: items ? items[0].BehaviourId : null,
                subBehaviourId: items ? items[0].SubBehaviourId : null,
                items,
            });
        };

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(message),
            actions: [
                { label: this._translateService.instant(labelYes), data: 0 },
                { label: this._translateService.instant(labelNo), data: 1 },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 1) {
                        actionNo();
                    }
                    if (ret === 0) {
                        actionYes();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _commitDialogPopUp(
        scenario: NavigationScenario,
        items: ObservationCommitDialogItem[],
    ): void {
        const ref = this._dialog.open<
            ObservationCommitDialogComponent,
            ObservationCommitDialogItem[],
            ObservationCommitDialogResult
        >(ObservationCommitDialogComponent, { data: items });
        ref.result
            .pipe(
                tap((result) => {
                    if (result.option === ObservationCommitOption.PUBLISH) {
                        this._publish(scenario, result);
                    } else if (
                        result.option === ObservationCommitOption.CANCEL
                    ) {
                        // Don't do anything just stay on observation form
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    save(): void {
        this.resolveConfirmationModal(
            TranslationKeyMap._9707_OBSERVATION_WILL_BE_SAVED_AS_DRAFT,
            ClickOptions.SAVEOBSERVATION,
        );
    }

    share(): void {
        this.resolveConfirmationModal(
            TranslationKeyMap._9708_DO_YOU_WANT_TO_SHARE_THIS_OBSERVATION_WITH_THE_PERFORMER_,
            ClickOptions.SHAREOBSERVATION,
        );
    }

    cancel(): void {
        // controls are not present in observation if observation-review or observation-pop-up is parent. just prevent routing
        // TODO change and check against this.state.mode
        if (this.mode !== ObservationMode.DEFAULT) {
            return;
        }

        this.resolveConfirmationModal(
            TranslationKeyMap._9709_DO_YOU_WANT_TO_CANCEL_THIS_OBSERVATION_,
            ClickOptions.CANCELOBSERVATION,
        );
    }

    delete(): void {
        this.resolveConfirmationModal(
            TranslationKeyMap._9710_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_OBSERVATION_,
            ClickOptions.DELETEOBSERVATION,
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resolveConfirmationModal(message: string, clickOption: number) {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(message),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9425_OK,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._121_CANCEL,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 0) {
                        return;
                    }

                    switch (clickOption) {
                        case ClickOptions.CANCELOBSERVATION:
                            this._cancel();
                            break;
                        case ClickOptions.DELETEOBSERVATION:
                            this._delete();
                            break;
                        case ClickOptions.SAVEOBSERVATION:
                            this._save();
                            break;
                        case ClickOptions.SHAREOBSERVATION:
                            this._share();
                            break;
                        default:
                            console.log('No valid click option');
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */ _onDismissMessageClick(): void {
        useDispatcher(this, rootDismissMessage({}));
    }

    /** @private */ _onUpsertCommitment(
        $event: ObservationCommitmentUpsertEvent,
    ): void {
        useDispatcher(
            this,
            rootNavigate({
                scenario: NavigationScenario.COMMITMENT,
                opts: {
                    focus: $event.focus,
                    withObservationId: true,
                    commitmentId: $event.commitmentId,
                },
            }),
        );
    }

    // TODO consider consolidating
    private _paramsWatcher(): Observable<Params> {
        return combineLatest([
            combineLatest([
                combineLatest(
                    this._activatedRoute.pathFromRoot.map((e) => e.params),
                ).pipe(
                    map((arr) =>
                        arr.reduce<Params>((p, c) => ({ ...p, ...c }), {}),
                    ),
                    tap((params) => {
                        this._userId = parseInt(params.userId);
                        this._observationId = parseInt(params.observationId);
                    }),
                ),
                this._activatedRoute.queryParams.pipe(
                    tap((queryParams) => {
                        if (this._userId == null || isNaN(this._userId)) {
                            this._userId = parseInt(queryParams.userId);
                        }
                        this._loadActive =
                            queryParams.loadActive === 'true' ||
                            queryParams.loadActive === '1';
                        this.draftCommitmentId = queryParams.draftCommitmentId;
                        this.commitmentId = isNaN(queryParams.commitmentId)
                            ? null
                            : parseInt(queryParams.commitmentId);
                        this.focus = reconstructTagFromIdentifierNext(
                            queryParams.focus,
                        );
                        this._fromOa =
                            queryParams.fromOa === 'true' ||
                            queryParams.fromOa === '1';
                        this._isGeneric =
                            queryParams.isGeneric === 'true' ||
                            queryParams.isGeneric === '1';

                        this._activeMetric = isNaN(queryParams.metricId)
                            ? null
                            : parseInt(queryParams.metricId);

                        if (queryParams.latestEventId) {
                            this._latestCommitmentEventId = parseInt(
                                queryParams.latestEventId,
                            );
                        }
                    }),
                ),
            ]),
            this._router.events.pipe(
                filter<NavigationEnd>(
                    (event) => event instanceof NavigationEnd,
                ),
                map((event) => event.url),
                startWith(this._router.url),
                pairwise(),
                filter((urls) => urls[0] === urls[1]),
                startWith([null, this._router.url]),
            ),
        ]);
    }

    private _watchDateTimeAndDuration(): void {
        const check = (): void => {
            this._minDate = new Date();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.state.coachingEvent &&
                this.state.coachingEvent.dateRestriction
            ) {
                this._minDate = subDays(
                    new Date(),
                    this.state.coachingEvent.dateRestriction.maxPastDays,
                );
            }
            this._maxDate = new Date(Date.now() + 30000);
            if (
                !this._form.form.disabled &&
                this._form.form.dirty &&
                !this._form.form.controls[ObservationFormFields.DURATION.name]
                    .dirty &&
                this.state.event.eventId == null
            ) {
                const duration =
                    this._form.form.value[ObservationFormFields.DURATION.name];
                this._form.form.patchValue(
                    {
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        [ObservationFormFields.DURATION.name]: duration + 1,
                    },
                    { emitEvent: false },
                );
            }
        };
        // using a property accessor would get called too often and then the control also invokes the fn passed in Validator.registerOnValidatorChange
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._maxDateSub && this._maxDateSub.unsubscribe();
        this._maxDateSub = interval(60000)
            .pipe(
                tap(() => check()),
                takeUntilDestroy(this),
            )
            .subscribe();
        check();
    }

    /* TODO revise. this is sort of dirty */
    private _focusFirstNote(): void {
        if (this.state?.event?.published === true) {
            return;
        }

        const notes = pluck<NoteDetailFormValue[]>(
            this.state.initialFormValue.tagGroups,
            'interactions[].notes[]',
        );
        if (!notes || !notes.length) {
            return;
        }

        const selector = `[data-trackingid='${notes[0].trackingId}'][data-ready="true"]`;
        this._focusService.registerSelector(selector, () => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const el = document.querySelector(selector) as HTMLElement;
            if (el) {
                el.focus();
            }
        });
    }

    private _patchForm(): void {
        this._attemptedToPublish = false;

        this._form.clearWarningsErrors();
        this._form.form.reset(this.state.initialFormValue || {}, {
            emitEvent: false,
        });

        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (this.state.event && this.state.event.published) ||
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (this._readonly && this._readonly.value)
        ) {
            this._form.form.disable({ emitEvent: false });
        } else {
            this._form.form.enable({ emitEvent: false });
        }

        this._watchDateTimeAndDuration();

        this._focusFirstNote();
    }

    // TODO consolidate
    private _askToContinue(
        currentState?: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        // no modal is open and nothing is pending and the form doesn't have unsaved changes
        const noPending =
            !this.state ||
            Object.entries(this.state.pending)
                .filter(([key]) => key !== 'config' && key !== 'reload')
                .every(([_, value]) => !value);
        const noUnSaved = !this._form.form.dirty;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const isDeleted = this.state && this.state.event.deleted;

        const canDeactivateWithoutConfirm =
            (noPending && noUnSaved) ||
            isDeleted ||
            this.extraOptions?.canDeactivateWithoutConfirm;
        let changedMetric = false;
        if (currentState && nextState) {
            const cRouteParams = currentState.root.queryParams;
            const nRouteParams = nextState.root.queryParams;
            const cParam = cRouteParams.metricId;
            const nParam = nRouteParams.metricId;
            changedMetric = cParam && nParam && cParam !== nParam;
        }

        // eslint-disable-next-line @typescript-eslint/promise-function-async
        const ask = (message, labelNo, labelYes): Promise<number> => {
            const data: CmpsConfirmDialogOptions = {
                title: this._translateService.instant(
                    TranslationKeyMap._9492_WARNING,
                ),
                message: this._translateService.instant(message),
                actions: [
                    { label: this._translateService.instant(labelNo), data: 1 },
                    {
                        label: this._translateService.instant(labelYes),
                        data: 0,
                    },
                ],
            };
            return this._dialog
                .open<
                    CmpsConfirmDialogComponent,
                    CmpsConfirmDialogOptions,
                    number
                >(CmpsConfirmDialogComponent, { data })
                .result.toPromise();
        };

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const finalResult = (ret: number) => {
            return ret === 0;
        };

        if (changedMetric) {
            return ask(
                TranslationKeyMap._9494_YOU_ARE_SELECTING_A_DIFFERENT_METRIC__THIS_WILL_TAKE_YOU_OUTSIDE_THIS_FORM_,
                TranslationKeyMap._121_CANCEL,
                TranslationKeyMap._9425_OK,
            ).then((ret1) => {
                if (ret1 === 0 && !canDeactivateWithoutConfirm) {
                    return ask(
                        TranslationKeyMap._9489_YOU_ARE_LEAVING_THIS_SESSION_,
                        TranslationKeyMap._9491_STAY,
                        TranslationKeyMap._9490_LEAVE,
                    ).then((ret2) => {
                        return finalResult(ret2);
                    });
                }
                return finalResult(ret1);
            });
        }

        if (canDeactivateWithoutConfirm) {
            return true;
        }

        return ask(
            TranslationKeyMap._9489_YOU_ARE_LEAVING_THIS_SESSION_,
            TranslationKeyMap._9491_STAY,
            TranslationKeyMap._9490_LEAVE,
        ).then((ret2) => {
            return finalResult(ret2);
        });
    }

    private _publish(
        scenario: NavigationScenario,
        publishOptionResult?: ObservationCommitDialogResult,
    ): void {
        this._form.checkWarningsErrors();
        const skipValidity =
            this.state.mode === ObservationMode.REVIEW_READ_ONLY; // form should be valid when read only (disabled)
        if (!skipValidity && !this._form.form.valid) {
            return;
        }

        let behaviour;
        let subBehaviour;
        if (publishOptionResult) {
            behaviour = publishOptionResult.behaviourId;
            subBehaviour = publishOptionResult.subBehaviourId;
        }

        const actions = [rootNavigate({ scenario })];
        useDispatcher(
            this,
            rootSave({
                formValue: this._form.form.value,
                share: undefined,
                publish: true,
                actions,
                primaryBehaviourId: behaviour,
                primarySubBehaviourId: subBehaviour,
                latestCommitmentEventId: this._latestCommitmentEventId,
                activeMetricId: this._activeMetric,
                isGeneric: this._isGeneric,
                dialogItems: publishOptionResult.items,
            }),
        );
    }

    private _save(): void {
        this._form.form.updateValueAndValidity();
        this._form.checkWarningsErrors();
        if (
            (!this._form.form.valid && !this.state.event.published) ||
            // let disabled but non-invalid forms pass for already-published-observation scenario
            (this._form.form.invalid &&
                this._form.form.disabled &&
                this.state.event.published)
        ) {
            return;
        }
        const actions =
            this.state.mode === ObservationMode.DEFAULT &&
            this.state.event.eventId == null
                ? [rootNavigate({ scenario: NavigationScenario.NEWLY_SAVED })]
                : undefined;
        useDispatcher(
            this,
            rootSave({
                formValue: this._form.form.value,
                share: undefined,
                publish: undefined,
                actions,
            }),
        );
    }

    private _share(): void {
        // state.allowedActions already has checks
    }

    private _cancel(): void {
        // TODO revise
        const routePath = [
            ...this._activatedRoute.snapshot.pathFromRoot,
        ].reverse();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const queryParams = this._activatedRoute.snapshot.queryParams;

        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            routePath[0] &&
            routePath[0].queryParams &&
            routePath[0].queryParams.backOnCancel !== undefined
        ) {
            this._location.back();
            return;
        }
        if (
            routePath.find(
                (e) =>
                    e.routeConfig && e.routeConfig.path === 'coaching-detail',
            )
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            navigateToDetail(this._router, this._activatedRoute, false);
            return;
        }
        this._location.back();
    }

    private _delete(): void {
        useDispatcher(
            this,
            rootDelete({
                observationId: this.state.event.eventId,
                actions: [
                    rootNavigate({ scenario: NavigationScenario.DELETE }),
                ],
            }),
        );
    }

    // TODO this method still needs lots of refactoring
    private _navigate(): void {
        const dialogItems = getDialogItems(
            this._form.form.value,
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state && this.state.event && this.state.event.eventId != null,
            this.state.behaviours,
            this.state.subBehaviours,
            this._switchboardService,
        );
        const { statuses, promiseStatuses } = prepStatuses(
            this.state.event.published,
            dialogItems,
            this._switchboardService,
            this.state.draftIdByIdentifier,
        );

        let primaryFocusContainer = this.state.focusContainers.filter(
            (x) => x.isPrimary,
        )[0];
        if (!primaryFocusContainer) {
            primaryFocusContainer = [...this.state.focusContainers].sort(
                (a, b) => a.focusId - b.focusId,
            )[0];
        }

        if (
            this.state.navigationScenario ===
            NavigationScenario.NEWLY_PUBLISHED_NAVIGATE
        ) {
            goToDraftOrCommitment(
                this.state.nextCommitmentDraftId,
                this.state.nextCommitmentId,
                this._router,
                this._activatedRoute,
                this._switchboardService,
                statuses,
                promiseStatuses,
            );
        }

        if (
            this.state.navigationScenario ===
            NavigationScenario.NO_FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._switchboardService.commitment.sbSetFocus(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                {
                    behaviourId: primaryFocusContainer.behaviourId,
                    subBehaviourId: primaryFocusContainer.subBehaviourId,
                    focusId: primaryFocusContainer.focusId,
                    metrics: primaryFocusContainer.metrics.map(
                        (x) => x.metricId,
                    ),
                    userId: this.state.performer.userId,
                } as FocusDto,
            );
        }

        if (
            this.state.navigationScenario ===
                NavigationScenario.NO_FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK ||
            this.state.navigationScenario ===
                NavigationScenario.FOCUS_SUPPLIED_NEWLY_PUBLISHED_LINK
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._switchboardService.commitment.sbLinkFocus(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                {
                    behaviourId: primaryFocusContainer.behaviourId,
                    subBehaviourId: primaryFocusContainer.subBehaviourId,
                } as FocusShallow,
                this.state.event.eventId,
            );

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._switchboardService.pillarTwo.sbReload(TabRefId.OBSERVATIONS);

            // TODO bubble an event up to the parent instead of doing this here
            this._dialogRef && (this._dialogRef as any)._closePrivate();
        }

        if (
            this.state.navigationScenario === NavigationScenario.NEWLY_SAVED ||
            this.state.navigationScenario ===
                NavigationScenario.NEWLY_PUBLISHED_STAY
        ) {
            if (this.mode === ObservationMode.DEFAULT) {
                this._form.form.markAsPristine();

                replaceStateToExistingObservation(
                    this._location,
                    this.state.event.eventId,
                );
                promiseStatuses(Promise.resolve())
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        if (
                            statuses.length === 0 &&
                            this._switchboardService.pillarTwo.connected()
                        ) {
                            // since replaceStateToExistingObservation is being used, status needs be set explicitly
                            return this._switchboardService.pillarTwo.sbSetStatuses(
                                [
                                    {
                                        which: TabRefId.OBSERVATIONS,
                                        id: this.state.event.eventId,
                                        type: undefined,
                                        identifier: undefined,
                                        active: true,
                                    },
                                ],
                            );
                        }
                        return Promise.resolve();
                    })
                    .then(() => {
                        if (statuses.length !== 0) {
                            this._switchboardService.pillarTwo.sbScrollIntoView(
                                TabRefId.COMMITMENTS,
                                statuses[0].id,
                                statuses[0].identifier,
                            );
                        } else {
                            this._switchboardService.pillarTwo.sbScrollIntoView(
                                TabRefId.OBSERVATIONS,
                                this.state.event.eventId,
                            );
                        }
                    })
                    .catch(() => null);
            } else {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._switchboardService.pillarTwo.sbReload(
                    TabRefId.OBSERVATIONS,
                );
            }
        }

        if (this.state.navigationScenario === NavigationScenario.COMMITMENT) {
            const params: Params = {};
            const observationId = this.state.event.eventId;

            if (
                this._switchboardService.commitment.connected() &&
                this._switchboardService.commitment.sbHasLinkedFocus(
                    this.state.navigationScenarioOpts.focus,
                    observationId,
                )
            ) {
                return;
            }
            if (this.state.navigationScenarioOpts.focus) {
                params.focus = composeTagIdentifierNext(
                    this.state.navigationScenarioOpts.focus,
                );
            }
            if (this.state.navigationScenarioOpts.withObservationId) {
                params.observationId = observationId;
            }
            if (this.state.navigationScenarioOpts.commitmentId != null) {
                const commitmentId =
                    this.state.navigationScenarioOpts.commitmentId;
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                navigateToExistingCommitment(
                    this._router,
                    this._activatedRoute,
                    commitmentId,
                    params.focus,
                    params.observationId,
                    undefined,
                    undefined,
                ).then(() => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._switchboardService.pillarTwo.connected()
                        ? this._switchboardService.pillarTwo.sbScrollIntoView(
                              TabRefId.COMMITMENTS,
                              composeCommitmentSummaryItemIdentifier({
                                  commitmentId,
                                  draftId: null,
                              }),
                          )
                        : Promise.resolve();
                });
            } else {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                navigateToNewDraftCommitment(
                    this._router,
                    this._activatedRoute,
                    undefined,
                    params.focus,
                    params.observationId,
                    undefined,
                );
            }
        }

        if (this.state.navigationScenario === NavigationScenario.DELETE) {
            /* eslint-disable @typescript-eslint/no-misused-promises */
            (
                this._switchboardService.pillarTwo.sbReload(
                    TabRefId.OBSERVATIONS,
                ) || Promise.resolve()
            )
                /* eslint-enable @typescript-eslint/no-misused-promises */
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then(() => {
                    return navigateToDetail(
                        this._router,
                        this._activatedRoute,
                        true,
                    );
                })
                .catch(() => null);
        }

        if (
            this.state.navigationScenario ===
                NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED ||
            this.state.navigationScenario ===
                NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED ||
            this.state.navigationScenario ===
                NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED ||
            this.state.navigationScenario ===
                NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED
        ) {
            this.extraOptions?.onNavigate();

            const doNotNavigate = this.extraOptions?.doNavigate();
            if (!doNotNavigate) {
                goToDraftOrCommitment(
                    this.state.nextCommitmentDraftId,
                    this.state.nextCommitmentId,
                    this._router,
                    this._activatedRoute,
                    this._switchboardService,
                    statuses,
                    promiseStatuses,
                );

                // TODO bubble an event up to the parent instead of doing this here... or use the switchboard
                this._dialog.closeAll();
            } else {
                // TODO bubble an event up to the parent instead of doing this here
                this._dialogRef && (this._dialogRef as any)._closePrivate();
            }
        }
    }
}
